export interface CreateNotificationInput {
  recipientUserId: string;
  type: string;
  title: string;
  message?: string;
  relatedId?: string;
  relatedType?: 'Quote' | 'Invoice' | 'Delivery' | 'PurchaseOrder' | 'Bill' | 'Task' | 'Contact' | 'Product';
  link?: string;
  severity?: 'info' | 'warning' | 'critical';
  threadId?: string;
  eventDate?: string;
  [key: string]: unknown;
}

const LOCAL_API_TOKEN_KEY = 'app_auth_token';

/**
 * Create a notification for a specific organization member in the local API.
 * Notification failures are intentionally isolated from the primary action: a
 * task, note, or discussion should not fail just because its alert could not
 * be delivered.
 */
export async function createLocalNotification(
  orgId: string,
  input: CreateNotificationInput,
): Promise<boolean> {
  if (typeof window === 'undefined') return false;
  const token = localStorage.getItem(LOCAL_API_TOKEN_KEY);
  if (!token || !orgId || !input.recipientUserId) return false;

  try {
    const response = await fetch(`/api/organizations/${orgId}/notifications`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(input),
    });
    if (!response.ok) {
      const payload = await response.json().catch(() => null);
      console.warn('Notification delivery failed:', payload?.error || response.status);
      return false;
    }
    return true;
  } catch (error) {
    console.warn('Notification delivery failed:', error);
    return false;
  }
}

export async function createLocalNotifications(
  orgId: string,
  recipientUserIds: Iterable<string>,
  notification: Omit<CreateNotificationInput, 'recipientUserId'>,
): Promise<void> {
  const recipients = [...new Set([...recipientUserIds].filter(Boolean))];
  const notificationFields = notification as CreateNotificationInput;
  await Promise.all(recipients.map(recipientUserId =>
    createLocalNotification(orgId, {
      ...notificationFields,
      recipientUserId,
      type: notificationFields.type,
      title: notificationFields.title,
    }),
  ));
}
